import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  ScrollView,
} from "react-native";
import { SearchBar } from "../components/browse/SearchBar";
import { CategoryBrowser } from "../components/browse/CategoryBrowser";
import { ProductGrid } from "../components/products/ProductGrid";
import { useProducts } from "../hooks/useProducts";
import { useNavigation, useRoute } from "@react-navigation/native";
import { typography, spacing } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { LoadingState } from "../components/ui/LoadingState";
import { EmptyState } from "../components/ui/EmptyState";
import { Feather } from "@expo/vector-icons";
import { CATEGORIES } from "../lib/categories";
import { ProductFilterPanel } from "../components/products/ProductFilterPanel";
import { ProductCategoryList } from "../components/products/ProductCategoryList";
import type { Category } from "../components/products/ProductCategoryList";
import type { Filters } from "../types/filters";
import type { RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../types/navigation";

type BrowseScreenRouteProp = RouteProp<RootStackParamList, "Browse">;

export default function BrowseScreen() {
  const route = useRoute<BrowseScreenRouteProp>();
  const navigation = useNavigation();
  const colors = useColors();
  const styles = createStyles(colors);

  // Animations - start at final values to prevent dimming issues during hot reload
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  const [searchQuery, setSearchQuery] = useState(
    route.params?.searchQuery || ""
  );
  const [selectedCategory, setSelectedCategory] = useState<
    string | undefined
  >();
  const [selectedSubcategory, setSelectedSubcategory] = useState<
    string | undefined
  >();
  const [selectedItem, setSelectedItem] = useState<string | undefined>();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [expandedSubcategory, setExpandedSubcategory] = useState<string | null>(
    null
  );

  // Add filter and sort state
  const [sortBy, setSortBy] = useState("newest");
  const [filters, setFilters] = useState<Filters>({
    condition: undefined,
    size: undefined,
    priceRange: undefined,
    rating: undefined,
    distance: undefined,
    main_category: undefined,
    category_subcategory: undefined,
    category_item: undefined,
  });
  const [isFilterActive, setIsFilterActive] = useState(false);

  // Add state for ProductCategoryList
  const [productCategorySelection, setProductCategorySelection] =
    useState("all");

  // Check if any filter is active
  useEffect(() => {
    const hasActiveFilter = Object.values(filters).some(
      (value) => value !== undefined
    );
    setIsFilterActive(hasActiveFilter);
  }, [filters]);

  const { data: products, isLoading } = useProducts({
    searchQuery,
    filters: {
      ...filters,
      main_category:
        productCategorySelection !== "all"
          ? productCategorySelection
          : filters.main_category,
    },
    sortBy: sortBy,
  });

  useEffect(() => {
    if (route.params?.searchQuery) {
      setSearchQuery(route.params.searchQuery);
      // Clear all filters and categories when searching
      setSelectedCategory(undefined);
      setSelectedSubcategory(undefined);
      setSelectedItem(undefined);
      setExpandedCategory(null);
      setExpandedSubcategory(null);
      setProductCategorySelection("all");
      // Reset all filters to default state
      setFilters({
        condition: undefined,
        size: undefined,
        priceRange: undefined,
        rating: undefined,
        distance: undefined,
        main_category: undefined,
        category_subcategory: undefined,
        category_item: undefined,
      });
      setSortBy("newest");
    }
  }, [route.params?.searchQuery]);

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    setSearchQuery(query);
    // Clear all filters and categories when searching
    setSelectedCategory(undefined);
    setSelectedSubcategory(undefined);
    setSelectedItem(undefined);
    setExpandedCategory(null);
    setExpandedSubcategory(null);
    setProductCategorySelection("all");
    // Reset all filters to default state
    setFilters({
      condition: undefined,
      size: undefined,
      priceRange: undefined,
      rating: undefined,
      distance: undefined,
      main_category: undefined,
      category_subcategory: undefined,
      category_item: undefined,
    });
    setSortBy("newest");
  };

  const handleCategorySelect = (
    categoryId: string,
    subcategoryId?: string,
    item?: string
  ) => {
    setSearchQuery("");
    setSelectedCategory(categoryId);
    setSelectedSubcategory(subcategoryId);
    setSelectedItem(item);

    // Keep track of expanded state
    setExpandedCategory(categoryId);
    if (subcategoryId) {
      setExpandedSubcategory(subcategoryId);
    }

    // Update filters based on the selected category/subcategory/item
    if (item) {
      setFilters((prev) => ({
        ...prev,
        main_category: categoryId,
        category_subcategory: subcategoryId,
        category_item: item,
      }));
    } else if (subcategoryId) {
      setFilters((prev) => ({
        ...prev,
        main_category: categoryId,
        category_subcategory: subcategoryId,
        category_item: undefined,
      }));
    } else if (categoryId) {
      setFilters((prev) => ({
        ...prev,
        main_category: categoryId,
        category_subcategory: undefined,
        category_item: undefined,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        main_category: undefined,
        category_subcategory: undefined,
        category_item: undefined,
      }));
    }
  };

  const handleBack = () => {
    if (selectedItem) {
      // When going back from an item, keep category and subcategory expanded
      setSelectedItem(undefined);
    } else if (selectedSubcategory) {
      setSelectedSubcategory(undefined);
      setExpandedSubcategory(null);
    } else if (selectedCategory) {
      setSelectedCategory(undefined);
      setExpandedCategory(null);
      setExpandedSubcategory(null);
    }
  };

  const getCurrentTitle = () => {
    if (selectedItem) {
      return selectedItem;
    }
    if (selectedSubcategory) {
      const category = CATEGORIES.find((c) => c.id === selectedCategory);
      const subcategory = category?.subcategories.find(
        (s) => s.id === selectedSubcategory
      );
      return subcategory?.name;
    }
    if (selectedCategory) {
      const category = CATEGORIES.find((c) => c.id === selectedCategory);
      return category?.name;
    }
    return "Browse";
  };

  // Handle ProductCategoryList selection
  const handleProductCategorySelect = (category: string) => {
    setProductCategorySelection(category);

    // When selecting from top category bar, update the filter panel's category
    if (category !== "all") {
      setFilters((prev) => ({
        ...prev,
        main_category: category,
      }));
    } else {
      // When selecting 'all', remove the category filter
      setFilters((prev) => ({
        ...prev,
        main_category: undefined,
      }));
    }
  };

  // Handle filter category selection from ProductFilterPanel
  const handleFilterCategorySelect = (filterCategory: string | undefined) => {
    // Update the filters with the new category
    setFilters((prev) => ({
      ...prev,
      main_category: filterCategory || undefined,
    }));

    // If selecting from filter panel, also update the top category selection
    if (filterCategory) {
      setProductCategorySelection(filterCategory);
    } else {
      setProductCategorySelection("all");
    }
  };

  // Handle all filter changes
  const handleFiltersChange = (newFilters: Filters) => {
    setFilters(newFilters);
  };

  // Handle clearing filters
  const handleClearFilters = () => {
    setFilters({
      condition: undefined,
      size: undefined,
      priceRange: undefined,
      rating: undefined,
      distance: undefined,
      main_category: undefined,
      category_subcategory: undefined,
      category_item: undefined,
    });
    setProductCategorySelection("all");
  };

  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          styles.container,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <View style={styles.header}>
          {!selectedItem && (
            <View style={styles.welcomeSection}>
              {!searchQuery ? (
                <>
                  <Text style={styles.welcomeGreeting}>Discover</Text>
                  <Text style={styles.welcomeSubtext}>Explore curated categories</Text>
                </>
              ) : (
                <>
                  <Text style={styles.welcomeGreeting}>Search Results</Text>
                  <Text style={styles.welcomeSubtext}>Showing results for "{searchQuery}"</Text>
                </>
              )}
            </View>
          )}

          {selectedItem ? (
            <View style={styles.categoryHeader}>
              <TouchableOpacity style={styles.backButton} onPress={handleBack}>
                <Feather name="chevron-left" size={24} color={colors.text} />
              </TouchableOpacity>
              <Text style={styles.categoryTitle} numberOfLines={1}>
                {getCurrentTitle()}
              </Text>
            </View>
          ) : (
            <View style={styles.searchContainer}>
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={handleSearch}
                placeholder="Search for products..."
              />
            </View>
          )}
        </View>

        <ScrollView
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}
        >
          {searchQuery ? (
            <View style={styles.searchFiltersContainer}>
              <ProductCategoryList
                selectedCategory={productCategorySelection}
                onSelectCategory={handleProductCategorySelect}
                onFilterCategorySelect={handleFilterCategorySelect}
              />
              <ProductFilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                sortBy={sortBy}
                onSort={setSortBy}
              />
            </View>
          ) : null}

          {!selectedItem && !searchQuery ? (
            <CategoryBrowser
              onSelectCategory={handleCategorySelect}
              expandedCategory={expandedCategory}
              expandedSubcategory={expandedSubcategory}
            />
          ) : isLoading ? (
            <LoadingState />
          ) : products && products.length > 0 ? (
            <View style={styles.productsSection}>
              <ProductGrid
                products={products}
                onProductPress={(product) =>
                  navigation.navigate("ProductDetails", { id: product.id })
                }
              />
            </View>
          ) : (
            <EmptyState
              icon="search"
              title="No Results Found"
              description={
                searchQuery
                  ? "Try adjusting your search terms or filters"
                  : "No products found in this category"
              }
            />
          )}
        </ScrollView>
      </Animated.View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    content: {
      flexGrow: 1,
      paddingBottom: spacing.xl,
    },
    welcomeSection: {
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      paddingTop: spacing.md,
      paddingBottom: spacing.sm,
    },
    welcomeGreeting: {
      fontSize: 28,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    welcomeSubtext: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 2,
      fontWeight: "500",
    },
    header: {
      backgroundColor: colors.background,
      zIndex: 1,
    },
    searchContainer: {
      paddingBottom: spacing.sm,
      backgroundColor: colors.background,
      marginBottom: spacing.sm,
    },
    searchFiltersContainer: {
      backgroundColor: colors.background,
      zIndex: 1,
      paddingTop: spacing.xs,
    },
    productsSection: {
      paddingTop: spacing.xs,
    },
    categoryHeader: {
      flexDirection: "column",
      gap: 12,
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
    },
    backButton: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    backText: {
      ...typography.body,
      color: colors.text,
      fontSize: 16,
    },
    categoryTitle: {
      fontSize: 22,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: -0.5,
    },
  });
